/* global Sys, Utils, EventHandler, Platform */
Sys.ns("Utils");

/**
 * Platform utilities. This detects features in the browser and returns values based on said features.
 *
 * Supported devices:
 *
 * | Device            | OS                              | Browser                              |
 * | ----------------- | ------------------------------- | ------------------------------------ |
 * | Smartphone/tablet | iOS 9.3+                        | Safari (zoomed and standard view)    |
 * |                   |                                 | WebApp (saved to home screen)        |
 * |                   |                                 | Chrome for iOS (latest version)      |
 * |                   | Android 4.4+                    | Chrome (56+)                         |
 * |                   |                                 | Stock browser (Samsung Browser 4.0+) |
 * | Desktop           | Windows 7+                      | Chrome (56+) (flash 25+)             |
 * |                   |                                 | IE 11+ (flash 25+)                   |
 * |                   |                                 | Firefox (latest version) (flash 25+) |
 * |                   | Windows 10+                     | Chrome (56+) (flash 25+)             |
 * |                   |                                 | Edge (latest version) (flash 25+)    |
 * |                   |                                 | Firefox (latest version) (flash 25+) |
 * |                   | OS X 10.9+                      | Safari 9+ (flash 25+)                |
 * |                   |                                 | Chrome (56+) (flash 25+)             |
 */
Utils.Platform = {

    DEBUG: false,
    LANDSCAPE: "LANDSCAPE",
    PORTRAIT: "PORTRAIT",
    ENABLE_TABLET_PORTRAIT: false,

    /**
     * Binds event listeners on window for detecting various window event changes such as
     * orientation, page visibility and resizing.
     *
     * @returns {void}
     */
    init: function() {
        var me = this,
            pageVisibility;

        window.addEventListener("orientationchange", function() {
            var screenOrientation = (screen.orientation || {}).type || screen.mozOrientation || screen.msOrientation,
                currentOrientation,
                start = null,
                progress = 0,
                dispatchOrientationChangeEvent = function(orientation) {
                    if (me.DEBUG) {
                        console.info("Orientation changed to:", orientation);
                    }
                    EventHandler.dispatchEvent("notify:platform.orientationChanged", orientation);
                },
                animationFrameStep = function(timestamp) {
                    if (!start) {
                        start = timestamp;
                    }

                    progress = (typeof (timestamp - start) !== "number") ? 0 : (timestamp - start);

                    if (progress < 150) {
                        window.requestAnimationFrame(animationFrameStep);
                    }
                    else {
                        currentOrientation = me.getOrientation();
                        dispatchOrientationChangeEvent(currentOrientation);
                        start = null;
                    }
                };

            if (me.DEBUG) {
                console.info("Screen orientation:", screenOrientation);
            }

            if (typeof screenOrientation !== "undefined") {
                currentOrientation = me.getOrientation(screenOrientation);
                dispatchOrientationChangeEvent(currentOrientation);
            }
            else {
                window.requestAnimationFrame(animationFrameStep);
            }
        }, false);

        window.addEventListener("resize", function() {
            if (me.DEBUG) {
                console.info("Resize triggered.");
            }

            EventHandler.dispatchEvent("notify:platform.resized");
        }, false);

        pageVisibility = this.getPageVisibilityAPI();
        if (pageVisibility) {
            document.addEventListener(pageVisibility.event, function() {
                var hidden = document[pageVisibility.type];

                if (me.DEBUG) {
                    console.info("Page visibility changed to:", hidden);
                }

                EventHandler.dispatchEvent("notify:platform.visibilityChanged", hidden);

                // @deprecated this event is only fired for legacy reasons
                EventHandler.dispatchEvent("pageVisibilityChanged_event", hidden);
            }, false);
        }
    },

    /**
     * Retrieves browser orientation in a cross-browser compatible way.
     * Still needs some logic to handle orientation specifics when executed in an iframe.
     *
     * @param {string} [screenOrientation] Screen orientation value.
     * @returns {string} Orientation string (LANDSCAPE|PORTRAIT).
     */
    getOrientation: function(screenOrientation) {
        if (Platform.isDesktopDevice || (Platform.isTabletDevice && !this.ENABLE_TABLET_PORTRAIT)) {
            return this.LANDSCAPE;
        }

        if (typeof screenOrientation !== "undefined") {
            if (screenOrientation.indexOf("landscape") !== -1) {
                return this.LANDSCAPE;
            }
            else if (screenOrientation.indexOf("portrait") !== -1) {
                return this.PORTRAIT;
            }
        }

        if (document.documentElement.clientWidth >= document.documentElement.clientHeight) {
            return this.LANDSCAPE;
        }

        return this.PORTRAIT;
    },

    /**
     * Checks if given orientation is landscape. If orientation is undefined, will retrieve orientation from the browser
     * and check that one instead.
     *
     * @param {string} [orientation] Orientation value.
     * @returns {boolean} Whether or not passed value or browser is in landscape.
     */
    isLandscape: function(orientation) {
        return typeof orientation !== "undefined" ? orientation === this.LANDSCAPE : this.getOrientation() === this.LANDSCAPE;
    },

    /**
     * Checks if given orientation is portrait. If orientation is undefined, will retrieve orientation from the browser
     * and check that one instead.
     *
     * @param {string} [orientation] Orientation value.
     * @returns {boolean} Whether or not passed value or browser is in portrait.
     */
    isPortrait: function(orientation) {
        return typeof orientation !== "undefined" ? orientation === this.PORTRAIT : this.getOrientation() === this.PORTRAIT;
    },

    /**
     * Checks if browser is currently in an iframe. Try and catch exists because browsers can block window.top due to
     * same origin policy. IE bugs may also be present.
     *
     * @returns {boolean} Whether or not in an iframe.
     */
    inIframe: function() {
        try {
            return window.self !== window.top;
        }
        catch (e) {
            return true;
        }
    },

    /**
     * Checks if current device is touch supported or not.
     *
     * @returns {boolean} Whether or not this is a touch device.
     *
     * Vendor prefix 'msMaxTouchPoints' should be removed in upcoming releases since support was removed in IE11.
     */
    isTouchSupported: function() {
        return (("ontouchstart" in window) ||
            ("ontouchstart" in document.documentElement) ||
            (navigator.maxTouchPoints > 0) ||
            (navigator.msMaxTouchPoints > 0));
    },

    /**
     * Retrieves input events related to the device being used, i.e. touch or not.
     *
     * @returns {Object} Object containing input events: down for pressing down and up for releasing.
     */
    getInputEvents: function() {
        var touchSupport = this.isTouchSupported();

        return {
            down: touchSupport ? "touchstart" : "mousedown",
            up: touchSupport ? "touchend" : "mouseup"
        };
    },

    /**
     * Checks if browser supports 3d transforms.
     *
     * @returns {boolean} Whether or not 3d transforms is supported.
     */
    is3DTransformSupported: function() {
        var el = document.createElement("p"),
            transforms = [
                "-webkit-transform",
                "-o-transform",
                "-ms-transform",
                "-moz-transform",
                "transform"
            ],
            has3d = false;

        if (!window.getComputedStyle) {
            return false;
        }

        // Add it to the body to get the computed style
        document.body.insertBefore(el, null);

        transforms.forEach(function(t) {
            if (el.style[t]) {
                el.style[t] = "translate3d(1px,1px,1px)";
                has3d = window.getComputedStyle(el).getPropertyValue(t);
            }
        });

        document.body.removeChild(el);

        return has3d && has3d.length > 0 && has3d !== "none";
    },

    /**
     * @deprecated Will later be replaced by fscreen.
     * @see {@link https://github.com/rafrex/fscreen}
     * @returns {boolean} Whether or not full screen API is supported.
     */
    isFullScreenAPISupported: function() {
        var fn = ["exitFullscreen", "webkitExitFullscreen", "mozCancelFullScreen", "msExitFullscreen"];

        return fn.some(function(method) {
            return method in document;
        });
    },

    /**
     * Helper function for detectPlatformFeatures that determines if there is any web audio context available.
     *
     * @returns {?Object} If web audio context is available.
     */
    isWebAudioAPISupported: function() {
        return window.AudioContext || null;
    },

    /**
     * Checks if the platform supports vibration API.
     *
     * @returns {?Object} Whether or not vibration API is supported.
     */
    isVibrationAPISupported: function() {
        return window.navigator.vibrate || window.navigator.webkitVibrate || window.navigator.mozVibrate || null;
    },

    /**
     * Checks whether WebGL is supported in the browser or not.
     *
     * @see {@link http://www.studyjs.com/webgl/webglcontext.html}
     * @returns {boolean} True if WebGl is supported, false if not.
     */
    isWebGLSupported: function() {
        var canvas = document.createElement("canvas"),
            webglContextParams = ["webgl", "experimental-webgl", "webkit-3d", "moz-webgl"],
            webglContext = null,
            index;

        for (index = 0; index < webglContextParams.length; index++) {
            try {
                webglContext = canvas.getContext(webglContextParams[index]);
                if (webglContext) {
                    return true;
                }
            }
            catch (e) {
                return false;
            }
        }

        return false;
    },

    /**
     * Checks if page visibility API is supported in the browser.
     *
     * @returns {?Object} Object containing document property and event for detecting. Null if not supported.
     */
    getPageVisibilityAPI: function() {
        if (!document.hidden) {
            return {
                type: "hidden",
                event: "visibilitychange"
            };
        }
        else if (document.webkitHidden) {
            return {
                type: "webkitHidden",
                event: "webkitvisibilitychange"
            };
        }

        return null;
    },

    /**
     * Viewport is what the user can see in the browser and thus does not include scrollbars and such. This method
     * returns the value for the document element's client width and height and is regarded as cross-browser compatible.
     * 09.09.2019 - Implementation taken from verge, due to redundancy of verge package.
     *
     * @returns {{width: number, height: number}} Document element client width and height.
     */
    getViewportSize: function() {
        var docElement = document.documentElement,
            viewportW = function() {
                var a = docElement.clientWidth, b = window.innerWidth;

                return a < b ? b : a;
            },
            viewportH = function() {
                var a = docElement.clientHeight, b = window.innerHeight;

                return a < b ? b : a;
            };

        return {
            width: viewportW(),
            height: viewportH()
        };
    },

    /**
     * Gets the size (in pixels) of the browser window viewport including, if rendered, the vertical scrollbar.
     *
     * @returns {{width: number, height: number}} Returns the inner dimensions of the viewport.
     */
    getViewportInnerSize: function() {
        return {
            width: window.innerWidth,
            height: window.innerHeight
        };
    },

    /**
     * Gets the size of the outside of the browser window. It represents the size of the whole browser window including
     * sidebar (if expanded), window chrome and window resizing borders/handles.
     *
     * @returns {{width: number, height: number}} Returns the outer dimensions of the viewport.
     */
    getViewportOuterSize: function() {
        return {
            width: window.outerWidth,
            height: window.outerHeight
        };
    },

    /**
     * Gets the device size through the Screen interface. Note that device size is static and does not change when the
     * page is resized or rotated.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Screen}
     * @returns {?Object} Screen interface.
     */
    getDeviceSize: function() {
        return window.screen || null;
    },

    /**
     * Gets the device pixel ratio. This is used to determine how the resolution is interpreted by CSS.
     * The formula for calculating a device logical (or interpreted) resolution is:
     * logical resolution = physical resolution / device pixel ratio.
     *
     * @example
     * iPhone 6
     * Actual resolution: 750 x 1334
     * CSS pixel ratio: 2
     * Logical resolution: (750 / 2) x (1334 / 2) = 375 x 667
     *
     * @returns {?number} Device pixel ratio.
     */
    getDevicePixelRatio: function() {
        return window.devicePixelRatio || null;
    },

    /**
     * Checks if browser is in "standalone" mode or not. This is an iOS specific property for detecting whether the app
     * is saved to home screen or not.
     *
     * @returns {boolean} If stand alone or not.
     */
    isStandAlone: function() {
        return window.navigator.standalone;
    },

    handleNonCompliantScenarios: function() {
        var isAppleTouchDeviceInDesktopMode = this.isTouchSupported() && Platform.isMacintoshDevice;

        // The player should be sent back to the lobby with reason code 12,
        // If the game is launched in a iPad with Dekstop mode and without an Iframe
        if (isAppleTouchDeviceInDesktopMode && !this.inIframe()) {
            Sys.utils.goToLobby(Utils.CONSTANTS.REASON_CODES.NON_COMPLIANT);
        }
    }
};
